<template>
    <div class="bg-white shadow rounded mx-auto relative p-40 mb-40">
        <loading v-if="loading"></loading>
        <div v-else>
            <div>
                <div class="flex items-center mb-18 gap-8 text-black">
                    <p class="font-semibold text-body-sm">
                        {{ $t('jobs.create.share_job.header.link') }}
                        <span>
                            {{ translated(job.title, locale, true) }}
                            {{ location }}
                        </span>
                    </p>
                    <simple-button
                        icon="open-in-new"
                        look="icon"
                        small
                        @click="openPreview"
                    />
                </div>

               <div>
                   <h2 class="text-heading-1 mb-16">
                       {{ headerTitle }}
                   </h2>
                   <p class="text-body-sm text-black">
                       {{ headerDescription }}
                       <span
                           v-if="!job.is_published"
                           class="no-underline hover:underline cursor-pointer"
                           @click="goToPublishVacancy()"
                       >
                        {{ $t('jobs.publish_anyway') }}
                    </span>
                   </p>
               </div>
            </div>

            <!-- featured section -->
            <div v-if="featuredCards.length" class="my-40">
                <p class="text-heading-2 font-semibold text-black">
                    {{ $t('jobs.create.share_job.featuredCards.title') }}
                </p>
                <ShareJobSection
                    :job="job"
                    :cards="featuredCards"
                    @selectedCard="handleClick"
                />
            </div>

            <h1 class="text-heading-2 font-semibold text-black mb-24">
                {{ $t('jobs.create.share_job.sections.header.title') }}
            </h1>

            <div class="relative">
                <ShareJobNavigation
                    ref="navigation"
                    class="sticky top-0 z-100 overflow-hidden"
                    :sections="filteredSections"
                    @tab-selected="scrollToSection"
                ></ShareJobNavigation>

                <ShareJobSection
                    v-for="section in filteredSections"
                    :key="section.id"
                    :section="section"
                    :cards="section.cards"
                    :job="job"
                    :ref="'section-' + section.id"
                    class="share-job-section scroll-mt-160"
                    @selectedCard="handleClick($event)"
                />
            </div>

            <BoosterPopup
                v-if="isBoosterPopup"
                :card="selectedCard"
                :job="job"
                @close="closePopup"
            ></BoosterPopup>

            <integration-popup
                v-if="isIntegrationPopup"
                :card="selectedCard"
                :job="job"
                @close="closePopup"
            />

            <google-for-jobs-editor
                v-if="selectedPopup === 'google_for_jobs'"
                :card="selectedCard"
                :job="job"
                @close="closePopup"
            />

            <share-job-with-interims
                v-if="isInterimPopup"
                :card="selectedCard"
                :active-item="activeItemBySelection"
                :job="job"
                @close="closePopup"
            />
        </div>
    </div>
</template>

<script>
import ShareJobSection from './ShareJobSection.vue';
import ShareJobNavigation from './ShareJobNavigation.vue'
import * as booster from '../services/BoosterService';
import { index, getFavorites } from '../services/ShareJobService';
import ShareJobSectionModel from '../models/ShareJobSection';
import { normalizeJson } from '../../shared/helpers';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useNotificationsStore} from '../state/modules/notifications';
import ShareJobCardFavorite from '../models/ShareJobCardFavorite';
import Job from './Job.vue';
import Loading from './ui/Loading.vue';
import ShareJobCardItem from './ShareJobCardItem.vue';
import ShareJobCard from '../models/ShareJobCard';
import SvgIcon from './ui/SvgIcon.vue';
import Booster from "../models/Booster";
import BoosterPopup from './BoosterPopup.vue';
import {ADD_NOTIFICATION} from '../state/mutation-types';
import IntegrationPopup from "./IntegrationPopup.vue";
import * as vdab from "../services/VdabService";
import GoogleForJobsEditor from "./GoogleForJobsEditor.vue";
import ShareJobWithInterims from "./ShareJobWithInterims.vue";
import moment from "moment/moment";
import SimpleButton from "./ui/SimpleButton.vue";

export default {
    props: {
        job: Job,
        newJob: Boolean|String,
    },
    components:{
        SimpleButton,
        ShareJobWithInterims,
        GoogleForJobsEditor,
        IntegrationPopup,
        ShareJobSection,
        ShareJobCardItem,
        ShareJobNavigation,
        Loading,
        SvgIcon,
        BoosterPopup
    },
    data() {
        return {
            error: null,
            activeSection: 0,
            loading: true,
            isPopupVisible: false,
            selectedPopup: null,
            selectedCard: null,
            allowedBoosterTypeRefs: ['small', 'medium', 'large', 'video', 'holiday'],
            allowedIntegrationTypeRefs: ['vdab', 'multi-posting'],
            allowedInterimTypeRefs: ['external_partners', 'other_partners'],
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentUser', 'currentCompany', 'selectedLocale']),
        sections() {
            return useRepo(ShareJobSectionModel)
                .orderBy('order')
                .with('cards')
                .get();
        },
        filteredSections() {
            return this.sections.filter(section => !!section.cards.length);
        },
        locale() {
            return this.selectedLocale;
        },
        featuredCards() {
            let isPublished = this.job.is_published;
            let whereLabel = isPublished ? 'featured' : 'unpublished_featured'

            return useRepo(ShareJobCard).where(whereLabel, true).get();
        },
        headerTitle() {
            let isPublished = this.job.is_published;

            if (isPublished === true) {
                return this.$t('jobs.create.share_job.published.header.title')
            } else {
                return this.$t('jobs.create.share_job.not_published.header.title')
            }
        },
        headerDescription() {
            let isPublished = this.job.is_published;

            if (isPublished === true) {
                return this.$t('jobs.create.share_job.published.header.description')
            } else {
                return this.$t('jobs.create.share_job.not_published.header.description')
            }
        },
        activeItemBySelection() {
            return this.selectedPopup === 'external_partners' ? 'interim' : 'selection';
        },
        location() {
            return (this.job.active_location && this.job.active_location.city) ? `- ${this.job.active_location.city}` : null;
        },
        isBoosterPopup() {
            return this.allowedBoosterTypeRefs.includes(this.selectedPopup);
        },
        isIntegrationPopup() {
            return this.allowedIntegrationTypeRefs.includes(this.selectedPopup);
        },
        isInterimPopup() {
            return this.allowedInterimTypeRefs.includes(this.selectedPopup);
        },
        jobsite_locale() {
            if (this.currentCompany.jobsite.active_languages.includes(this.locale)) {
                return this.locale;
            }

            if (this.activeLocale.length && this.activeLocale[0]['value']) {
                return this.activeLocale[0]['value'];
            }

            return this.locale;
        },
        activeLocale() {
            let activeLocale = [];

            for (let locale in this.currentCompany.jobsite.active_languages) {
                activeLocale.push(this.currentCompany.jobsite.active_languages[locale]);
            }

            return activeLocale.map(locale => ({
                label: this.$t('languages.' + locale + '.title'),
                value: locale,
            }));
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        goToPublishVacancy() {
            if (this.$route.name === 'free-trial') {
                return;
            }

            if (this.newJob || this.$route.name === 'jobs.edit') {
                return this.$emit('back');
            }

            this.$router.push({
                name: 'jobs.edit',
                params: {
                    id: this.job.id,
                },
                query: {
                    step: 4,
                },
            });
        },
        openPreview() {
            if (!this.job.url[this.jobsite_locale]) {
                return this[ADD_NOTIFICATION]({
                    type: 'error',
                    message: this.$t('jobs.preview_blocked', {lang: this.$t('languages.' + this.jobsite_locale + '.title')}),
                });
            }

            //show with no hash
            let url = this.job.url[this.jobsite_locale];

            //check is jobsite is published in this language.
            if (!this.job.published || !this.job.active[this.jobsite_locale] || (this.job.schedule && moment().utc().isBefore(moment.utc(this.job.start_date)))) {
                url += '?previewHash=' + this.job.previewHash;
            }

            window.open(url, '_blank').focus();
        },
        fetchSections() {
            this.loading = true;

            index()
                .then((data) => {
                    useRepo(ShareJobSectionModel).save(normalizeJson(data));
                    this.loading = false; // Data loading complete
                })
            .catch((error) => {
                this.loading = false; // Even if there's an error, stop loading
            })
        },
        fetchFavorites() {
            getFavorites()
                .then(data => {
                    // Normalize the JSON data if necessary
                    let normalizedData = normalizeJson(data);

                    return normalizedData.forEach(favoriteCard => {
                        return useRepo(ShareJobCardFavorite)
                            .save({user_id: this.currentUser.id, share_job_card_id: favoriteCard.id});
                    });
                })
        },
        scrollToSection(sectionId) {
            const section = this.$refs['section-' + sectionId];
            if (section) {
                section[0].$el.scrollIntoView();
            }
        },
        handleClick(card) {
            this.selectedCard = card;
            this.selectedPopup = card.type_ref;
        },
        closePopup() {
            this.selectedCard = null;
            this.selectedPopup = null;
        },
        setScrollHandler() {
            let scrollEl = document.getElementsByClassName('overflow-y-scroll')[0];

            scrollEl.onscroll = () => {
                const sections = [...document.querySelectorAll('.share-job-section')];

                const observer = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (entry.isIntersecting) {
                                const sectionId = entry.target.dataset.sectionId;
                                if (sectionId) {
                                    this.$refs.navigation.activeTab = Number(sectionId);
                                }
                            }
                        });
                    },
                    {
                        rootMargin: '-50% 0px',
                    },
                );

                sections.forEach((section) => {
                    observer.observe(section);
                });
            }
        }
    },
    created() {
        this.fetchSections();
        this.fetchFavorites();
    },
    mounted() {
        this.setScrollHandler();

        booster.index().then(result => {
            return useRepo(Booster).save(normalizeJson(result.data));
        }).then(() => {
            this.fetchingBoosters = false;
        });
    },
}
</script>

<style></style>
